import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../contexts/AppContext";
import QuestionCard from "./QuestionCard";
import ResultModal from "./ResultModal";
import ExitConfirmationModal from "./ExitConfirmationModal";
import { siteConfig } from "../../config/siteConfig";

const ExamMode = () => {
  const { currentExam, setCurrentExam, setView, addExamResult, updateTopicProgress } = useApp();

  const [showResult, setShowResult] = useState(false);
  const [timer, setTimer] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [examKey, setExamKey] = useState(Date.now());
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // Cet examen a-t-il deja ete comptabilise ? Un ref (pas un state) : il est lu
  // dans des effets, ou un state serait fige par la closure. Sans lui, revenir
  // jusqu'a la derniere question en mode revision rappelle finishExam ->
  // addExamResult, et exam_count monte a chaque boucle.
  const countedRef = useRef(false);

  const isSimulator = currentExam?.sourceId === "simulator";
  const SIMULATOR_DURATION_SECONDS = siteConfig.exam.simulatorDurationMinutes * 60;

  useEffect(() => {
    if (currentExam) {
      const hasPreAnswered = currentExam.questions.some((q) => q.answered === true);

      if (hasPreAnswered) {
        const cleanQuestions = currentExam.questions.map((q) => ({
          ...q,
          answered: false,
          selectedAnswer: null,
        }));

        setCurrentExam((prev) => ({
          ...prev,
          questions: cleanQuestions,
          score: 0,
          currentIdx: 0,
        }));
      }

      const initialAnswered = {};
      currentExam.questions.forEach((_, index) => {
        initialAnswered[index] = {
          answered: false,
          selectedAnswer: null,
          isCorrect: false,
        };
      });

      setAnsweredQuestions(initialAnswered);
      setTimer(isSimulator ? SIMULATOR_DURATION_SECONDS : 0);
      setShowResult(false);
      setIsReviewMode(false);
      setExamKey(Date.now());
      countedRef.current = false; // nouvelle tentative : reste a comptabiliser
    }
  }, [currentExam?.sourceId, currentExam?.title]);

  useEffect(() => {
    let interval;
    // `!countedRef.current` : empeche le chrono de repartir sur un examen
    // deja termine si le modal de resultat venait a se fermer.
    if (currentExam && !showResult && !isReviewMode && !countedRef.current) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (isSimulator) {
            if (prev <= 1) {
              clearInterval(interval);
              finishExam();
              return 0;
            }
            return prev - 1;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentExam, showResult, isSimulator, isReviewMode]);

  const handleAnswer = (selectedIdx) => {
    if (!currentExam || isReviewMode) return;
    const currentIdx = currentExam.currentIdx;
    const isCorrect = selectedIdx === currentExam.questions[currentIdx].c;

    const previousState = answeredQuestions[currentIdx];
    const wasCorrect = previousState?.isCorrect || false;

    setAnsweredQuestions((prev) => ({
      ...prev,
      [currentIdx]: { answered: true, selectedAnswer: selectedIdx, isCorrect },
    }));

    setCurrentExam((prev) => {
      if (!prev) return prev;
      const updatedQuestions = [...prev.questions];
      updatedQuestions[currentIdx] = {
        ...updatedQuestions[currentIdx],
        answered: true,
        selectedAnswer: selectedIdx,
      };

      let scoreAdjustment = 0;
      if (isCorrect && !wasCorrect) scoreAdjustment = 1;
      else if (!isCorrect && wasCorrect) scoreAdjustment = -1;

      return { ...prev, questions: updatedQuestions, score: prev.score + scoreAdjustment };
    });
  };

  const handleNext = () => {
    if (!currentExam) return;
    const nextIdx = currentExam.currentIdx + 1;
    if (nextIdx < currentExam.questions.length) {
      setCurrentExam((prev) => ({ ...prev, currentIdx: nextIdx }));
    } else {
      finishExam();
    }
  };

  const handlePrevious = () => {
    if (!currentExam) return;
    const prevIdx = currentExam.currentIdx - 1;
    if (prevIdx >= 0) {
      setCurrentExam((prev) => ({ ...prev, currentIdx: prevIdx }));
    }
  };

  const finishExam = () => {
    if (!currentExam) return;

    // Deja comptabilise : on se contente de reafficher le resultat (cas d'une
    // revision menee jusqu'a la derniere question, ou handleNext rappelle
    // finishExam). Sans ca, exam_count montait a chaque boucle.
    if (countedRef.current) {
      setShowResult(true);
      return;
    }
    countedRef.current = true;
    const finalCorrect = Object.values(answeredQuestions).filter((q) => q.isCorrect).length;
    const totalQuestions = currentExam.questions.length;
    const percentage = Math.round((finalCorrect / totalQuestions) * 100);

    if (currentExam.sourceId !== "simulator") {
      updateTopicProgress(currentExam.sourceId, percentage);
    } else {
      addExamResult(finalCorrect, totalQuestions);
    }
    setShowResult(true);
  };

  const handleReview = () => {
    setShowResult(false);
    setIsReviewMode(true);
    setCurrentExam((prev) => ({ ...prev, currentIdx: 0 }));
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!currentExam) return null;

  const currentIdx = currentExam.currentIdx || 0;
  const currentQuestion = currentExam.questions[currentIdx];
  const progress = ((currentIdx + 1) / currentExam.questions.length) * 100;
  const isCurrentQuestionAnswered = answeredQuestions[currentIdx]?.answered || false;
  const selectedAnswer = answeredQuestions[currentIdx]?.selectedAnswer;
  const totalAnswered = Object.values(answeredQuestions).filter((q) => q.answered).length;
  const totalCorrect = Object.values(answeredQuestions).filter((q) => q.isCorrect).length;
  const effectiveness = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;

  return (
    <div key={examKey} className="fixed inset-0 bg-white z-[60] overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => setShowExitConfirmation(true)} className="p-3 hover:bg-slate-100 rounded-2xl">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div>
              <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">
                {isSimulator ? `⚡ ${siteConfig.exam.simulatorTitle.toUpperCase()}` : "📚 PRÁCTICA POR TEMA"}
              </p>
              <h2 className="text-xl font-black">{currentExam.title}</h2>
            </div>
          </div>
          <div className={`px-6 py-3 rounded-2xl font-mono font-bold text-lg text-white ${isSimulator && timer < 300 ? "bg-red-600 animate-pulse" : "bg-slate-900"}`}>
            {isSimulator ? "⏳" : "⏱️"} {formatTime(timer)}
          </div>
        </div>

        <div className="mb-8">
          <div className="flex justify-between mb-3">
            <span className="text-sm font-bold">
              Pregunta {currentIdx + 1} de {currentExam.questions.length}
            </span>
            <span className="text-sm font-bold text-blue-600">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500 rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {currentQuestion && (
          <QuestionCard
            key={`${examKey}-${currentIdx}`}
            question={currentQuestion}
            onAnswer={handleAnswer}
            isAnswered={isCurrentQuestionAnswered}
            selectedAnswer={selectedAnswer}
            showFeedback={isReviewMode ? true : !isSimulator}
          />
        )}

        <div className="mt-8 flex items-center justify-between">
          <button onClick={handlePrevious} disabled={currentIdx === 0} className="px-6 py-3 bg-white border-2 border-slate-200 rounded-xl font-bold disabled:opacity-30 hover:bg-slate-50 transition-all">
            ← Anterior
          </button>
          <button onClick={handleNext} disabled={!isCurrentQuestionAnswered} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold disabled:opacity-30 hover:bg-blue-700 transition-all">
            {currentIdx === currentExam.questions.length - 1 ? "Finalizar" : "Siguiente →"}
          </button>
        </div>

        <div className="mt-6 p-4 bg-slate-50 rounded-xl grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-slate-500">Respondidas</p>
            <p className="text-xl font-bold">
              {totalAnswered}/{currentExam.questions.length}
            </p>
          </div>
          {!isSimulator || isReviewMode ? (
            <>
              <div>
                <p className="text-xs text-slate-500">Correctas</p>
                <p className="text-xl font-bold text-green-600">{totalCorrect}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Efectividad</p>
                <p className="text-xl font-bold text-blue-600">{effectiveness}%</p>
              </div>
            </>
          ) : (
            <div className="col-span-2 flex items-center justify-center">
              <p className="text-xs font-bold text-slate-400 italic">
                Modo Simulador: Resultados al finalizar
              </p>
            </div>
          )}
        </div>

        <ExitConfirmationModal
          isOpen={showExitConfirmation}
          onConfirm={() => {
            setShowExitConfirmation(false);
            setView("dashboard");
            setCurrentExam(null);
          }}
          onCancel={() => setShowExitConfirmation(false)}
        />

        {showResult && (
          <ResultModal
            score={totalCorrect}
            totalQuestions={currentExam.questions.length}
            onClose={() => setShowResult(false)}
            onReview={handleReview}
            isTimeUp={isSimulator && timer === 0}
          />
        )}
      </div>
    </div>
  );
};

export default ExamMode;
